import { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { setPdfPasswordPromptHandler } from "@/utils/pdf_password_prompt";

import "./pdf_password_prompt.css";

/**
 * App-level modal asking for the password of a protected PDF during upload.
 * Mount once; upload services open it via promptPdfPassword().
 */
export function PdfPasswordPrompt() {
  const [request, setRequest] = useState(null); // { code, fileName, resolve }
  const [password, setPassword] = useState("");
  const inputRef = useRef(null);

  useEffect(
    () =>
      setPdfPasswordPromptHandler(
        ({ code, fileName }) =>
          new Promise((resolve) => {
            setPassword("");
            setRequest({ code, fileName, resolve });
          })
      ),
    []
  );

  const finish = (value) => {
    request?.resolve(value);
    setRequest(null);
    setPassword("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    finish(password);
  };

  const name = request?.fileName || "This PDF";
  const isRetry = request?.code === "password_invalid";

  return (
    <Modal
      className="pdfPassword_modal"
      backdropClassName="pdfPassword_backdrop"
      show={!!request}
      onHide={() => finish(null)}
      onEntered={() => inputRef.current?.focus()}
      centered
      aria-labelledby="pdf-password-modal-title"
      data-testid="fincobox-pdf-password-modal"
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title id="pdf-password-modal-title">Password protected PDF</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            <b className="text-break">{name}</b> is password protected. Enter its password so we can
            read it. We don't store the password.
          </p>
          <Form.Group controlId="pdf-password-input">
            <Form.Label>PDF password</Form.Label>
            <Form.Control
              ref={inputRef}
              type="password"
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isInvalid={isRetry && !password}
              data-testid="fincobox-pdf-password-input"
            />
            <Form.Control.Feedback type="invalid">
              Incorrect password. Please try again.
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => finish(null)}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!password.trim()}
            data-testid="fincobox-pdf-password-submit"
          >
            Unlock &amp; upload
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
